import { cn } from "cn";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Render del texto del modelo.
 *
 * Antes esto era un render mínimo hecho a mano que dejaba todo como texto
 * plano. La intención era que una tabla escrita en markdown se viera mal, para
 * empujar al modelo a usar los componentes de datos (sección 9). Fue un error:
 * castigaba al usuario por una regla que pertenece al prompt, y de paso se
 * perdían negritas, listas y encabezados, que el modelo usa constantemente.
 *
 * Ahora se renderiza markdown completo, tablas incluidas. Que los datos vengan
 * de una herramienta y no transcritos sigue siendo responsabilidad del prompt
 * del sistema, donde se puede corregir sin empeorar la lectura.
 *
 * Cada etiqueta lleva sus utilidades aquí y no en una hoja aparte: el preflight
 * de Tailwind deja los elementos sin estilo, así que este mapa ES la tipografía
 * de la prosa del modelo y se lee entera de un vistazo.
 */
export function Markdown({ text }: { text: string }) {
  return (
    // El primer y el último hijo pierden su margen: quien coloca este bloque
    // decide su separación, no el markdown que haya dentro.
    <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children, ...props }) => (
            <p {...props} className="mb-3">
              {children}
            </p>
          ),
          h1: ({ children, ...props }) => (
            <h1 {...props} className={cn(HEADING, "text-xl")}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 {...props} className={cn(HEADING, "text-lg")}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 {...props} className={cn(HEADING, "text-base")}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 {...props} className={cn(HEADING, "text-sm")}>
              {children}
            </h4>
          ),
          ul: ({ children, ...props }) => (
            <ul {...props} className={cn(LIST, "list-disc")}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol {...props} className={cn(LIST, "list-decimal")}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            // Una lista anidada no repite el margen de bloque de la de fuera.
            <li {...props} className="my-0.5 [&>ol]:my-0.5 [&>ul]:my-0.5">
              {children}
            </li>
          ),
          strong: ({ children, ...props }) => (
            <strong {...props} className="font-semibold">
              {children}
            </strong>
          ),
          // Los enlaces del modelo se abren fuera y sin pasar referer.
          a: ({ children, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {children}
            </a>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              {...props}
              className="mb-3 border-l-2 pl-3.5 text-muted-foreground"
            >
              {children}
            </blockquote>
          ),
          // El `code` de dentro pierde su recuadro: el bloque ya es el recuadro.
          pre: ({ children, ...props }) => (
            <pre
              {...props}
              className="mb-3 overflow-x-auto rounded-md border bg-background p-3 text-xs leading-relaxed [&_code]:border-0 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-[length:inherit]"
            >
              {children}
            </pre>
          ),
          code: ({ children, ...props }) => (
            <code
              {...props}
              className="rounded-sm border bg-secondary px-1 py-px font-mono text-[0.88em]"
            >
              {children}
            </code>
          ),
          hr: (props) => <hr {...props} className="my-5 border-t" />,
          // Las tablas necesitan su propio scroll: en móvil se desbordan.
          table: ({ children, ...props }) => (
            <div className="mb-3 overflow-x-auto rounded-lg border">
              <table {...props} className="w-full border-collapse text-sm">
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th
              {...props}
              className="border-b bg-secondary px-3 py-1.5 text-left text-xs font-semibold tracking-wide whitespace-nowrap text-muted-foreground uppercase"
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              {...props}
              className="border-b px-3 py-1.5 text-left whitespace-nowrap"
            >
              {children}
            </td>
          ),
          tbody: ({ children, ...props }) => (
            <tbody {...props} className="[&>tr:last-child>td]:border-b-0">
              {children}
            </tbody>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

const HEADING = "mt-5 mb-2 leading-tight font-semibold";
const LIST = "mb-3 pl-5";
